'use client';

import { Breadcrumb } from '@lulalife/design-system';
import { Home01 } from '@lulalife/design-system/icons/line';
import { useRouter } from 'next/navigation';
import type { MouseEventHandler } from 'react';
import { TELEMETRY_MASK } from 'src/helper/telemetry-privacy';
import { DSSurface } from './DSSurface';
import { SETTINGS_PAGE_INSET } from './SettingsPage';

/** Where the "Settings" crumb goes: the settings index, which renders General. */
const SETTINGS_HOME = '/settings';

type SettingsBreadcrumbProps = {
  /** The settings page being viewed — the last crumb, and the current one. */
  current: string;
  /**
   * An optional crumb between "Settings" and `current`, for a page nested one
   * level deeper — e.g. `Settings / Users / {person's name}`. Optional and
   * additive: every existing caller omits it and renders the same fixed
   * three-crumb trail as before.
   */
  parent?: { label: string; href: string };
  className?: string;
};

/**
 * The settings trail: a home link, then "Settings", then the current page — or,
 * for a page nested one level deeper, an optional `parent` crumb in between
 * (`Settings / Users / {person's name}`).
 *
 * DS `Breadcrumb` does the rendering, so the size, the slash separators and the
 * trail/current colours are its own `--color-breadcrumb-*` tokens — the same
 * values the prototype's stylesheet declares. What this adds is the fixed
 * three-or-four-crumb shape every settings page shares, so a caller passes only
 * the page (and, optionally, the one section above it) it is on.
 *
 * `parent` IS A REAL LINK, for the same reason "Settings" is below: an anchor
 * with no href invites a click and does nothing. It shares the exact click
 * handling `navigateOnPlainClick` gives "Settings" — plain left click routes
 * client-side, a modified click is left to the browser — so both crumbs are
 * caught by the same unsaved-changes guard described below.
 *
 * The home icon is the first crumb, not the DS `icon` slot. Since DS 0.24.0 that
 * slot is decorative — wrapped in `aria-hidden="true"` — so a link placed there
 * stays focusable but has no accessible name. As a crumb it is a real, named
 * link inside the breadcrumb `nav`, and it routes client-side through the same
 * `navigateOnPlainClick` as the other crumbs.
 *
 * "SETTINGS" IS A REAL LINK, and has to be. It was previously a crumb with no
 * href at all, on the reasoning that the section has no index route and that a
 * second "Settings" link would make the e2e sidebar locator ambiguous. The first
 * half was simply wrong — `/settings` exists and renders General — and the second
 * was solved the wrong way round: DS renders an item without href as an anchor
 * with no href, which is styled like the other crumbs, invites a click, and then
 * does nothing at all. A browser sweep found exactly that, twice: click it with
 * unsaved changes and you got no navigation, no confirm, no error.
 *
 * THE E2E CONSTRAINT IS REAL AND IS HANDLED ON THE OTHER SIDE. Five Playwright
 * page objects expand the sidebar group with
 * `getByRole('link', { name: 'Settings' })`, and `navigateViaSidebar` runs while
 * the app is already ON a settings page — so an unscoped locator would now match
 * two elements and fail strict mode. Those locators are scoped to `.ls-sidebar`
 * (tests/e2e-local/page-objects/settings.page.ts and settingsBase.page.ts). If a
 * new page object resolves a sidebar entry by role and name alone, scope it the
 * same way rather than taking the href off this crumb again.
 *
 * IT ROUTES CLIENT-SIDE. DS renders `items[].href` as a plain anchor, so left to
 * itself the click is a full page load. `anchorProps.onClick` cancels that and
 * pushes instead, for the home crumb as well. The href stays on the element regardless, because that is what makes it a
 * real link for middle-click, open-in-new-tab, the keyboard, and for the unsaved
 * changes guard, which intercepts `a[href]` clicks in the capture phase
 * (src/views/Settings/useSettingsDirtyGuard.ts). When the guard holds a click it
 * stops propagation, so this handler never runs and no navigation happens until
 * the reader confirms; on confirm the guard replays the click and it does.
 *
 * `DSSurface` is the marker: this renders inside `.ls-inpage-header`, legacy
 * markup with no marker of its own, and the compatibility guards in
 * src/app/design-system-compat.css neutralise DS utilities (`min-w-0`, `m-0`,
 * `p-0`, …) outside a marked subtree.
 *
 * The list reset on that wrapper survives the scoped preflight, and has to.
 * DS `Breadcrumb` renders a bare `<ol>` and expects preflight to have zeroed it.
 * `src/app/design-system-preflight.css` now supplies preflight inside
 * `[data-lula-ds]` subtrees, but only in `@layer base` — and what indents this
 * list is Bootstrap's reboot, which is UNLAYERED and therefore still wins:
 * measured from a compile of the app's Sass, `ol, ul { padding-left: 2rem }` and
 * `ol, ul, dl { margin-bottom: 1rem }`. Preflight's own `margin: 0; padding: 0`
 * loses to both, so without these two the trail would still sit indented with a
 * gap under it. The important modifier is the remedy, per section 4 of
 * design-system-compat.css.
 *
 * What preflight DID take over is `list-style: none`, which reboot never set —
 * so no marker rule is needed here. (There were no visible markers to begin
 * with: DS gives every `<li>` `display: flex`, which generates no marker box.)
 */
export const SettingsBreadcrumb = ({ current, parent, className }: SettingsBreadcrumbProps) => {
  const router = useRouter();

  // Shared by "Settings" and the optional `parent` crumb: a plain left click routes
  // client-side; a modified click (middle-click, cmd/ctrl/shift/alt) is left to the
  // browser so it still opens a new tab rather than being hijacked.
  const navigateOnPlainClick =
    (href: string): MouseEventHandler<HTMLAnchorElement> =>
    (event) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      router.push(href);
    };

  return (
    // The inset keeps the crumbs on the same left edge as the page title below
    // them, which is how the prototype draws the two. This band
    // (`.ls-inpage-header`) supplies the same 16px as the content shell, so the
    // same 8px lands both on 24px.
    // APX-672: re-masked for session replay. This renders inside `.ls-header`,
    // which opts back in to recording as static chrome — but the trail's last
    // crumb is `current`, and on the user and role detail routes that is a real
    // person's name (src/views/Settings/Users/UserDetails/index.tsx builds it from
    // first_name + last_name). `parent.label` can be dynamic too. DS `Breadcrumb`
    // takes items as plain objects with no per-crumb class hook, so the whole
    // trail is masked rather than just the dynamic crumb. The cost is that
    // "Settings" and the section name read as censored text in a replay; the
    // alternative leaks a name into every settings recording.
    <DSSurface
      className={`flex min-w-0 ${SETTINGS_PAGE_INSET} [&_ol]:m-0! [&_ol]:p-0! ${TELEMETRY_MASK}`}
    >
      <Breadcrumb
        className={className}
        showIcon={false}
        items={[
          {
            label: <Home01 aria-hidden="true" width="100%" height="100%" strokeWidth={1} />,
            href: '/',
            anchorProps: {
              'aria-label': 'Home',
              // Replaces the DS crumb classes (anchorProps spreads after them) so the
              // home crumb keeps the icon-slot geometry. `text-icon-secondary` is
              // also the gate section 3 of src/app/design-system-compat.css matches
              // on to hand the colour back from Bootstrap's reboot `a` rule.
              className:
                'flex size-size-icon-md items-center justify-center rounded-sm text-icon-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus',
              onClick: navigateOnPlainClick('/'),
            },
          },
          {
            label: 'Settings',
            href: SETTINGS_HOME,
            anchorProps: { onClick: navigateOnPlainClick(SETTINGS_HOME) },
          },
          ...(parent
            ? [
                {
                  label: parent.label,
                  href: parent.href,
                  anchorProps: { onClick: navigateOnPlainClick(parent.href) },
                },
              ]
            : []),
          { label: current },
        ]}
      />
    </DSSurface>
  );
};

export default SettingsBreadcrumb;
